import createDebug from 'debug';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanDeactivateFn } from '@angular/router';
import { ConfirmationDialogComponent } from '@components/confirmation-dialog/confirmation-dialog.component';
import type { SupportCasePage } from '@pages/support-case/support-case.page';

const debug = createDebug('unsavedChangesGuard');

export const unsavedChangesGuard: CanDeactivateFn<SupportCasePage> = (
  supportCasePage,
) => {
  const matDialog = inject(MatDialog);
  return (async () => {
    const caseEdited = !!supportCasePage.supportCase?.hasUnsavedChanges;
    const commentTyped = !!supportCasePage.newComment?.trim();

    // Nothing pending — the overwhelmingly common case
    if (!caseEdited && !commentTyped) {
      return true;
    }

    debug('pending caseEdited %s commentTyped %s', caseEdited, commentTyped);
    const dialogRef = matDialog.open(
      ConfirmationDialogComponent.contentComponent,
      { disableClose: true },
    );
    const instance: any = dialogRef.componentInstance;
    instance.message = caseEdited
      ? commentTyped
        ? 'This case has changes that have not been saved, and a comment that has not been added. Leave the page and discard them?'
        : 'This case has changes that have not been saved. Leave the page and discard them?'
      : 'You have typed a comment that has not been added. Leave the page and discard it?';

    const leave = await new Promise<boolean>((resolve) => {
      // The user accepted losing the edits
      instance.confirmed.subscribe(() => {
        dialogRef.close();
        resolve(true);
      });

      // The user wants to stay and finish
      instance.canceled.subscribe(() => {
        dialogRef.close();
        resolve(false);
      });

      /**
       * Treat any other close as a cancel
       *
       * `resolve` after the first call is a no-op, so the two subscriptions
       * above still win when they are what closed the dialog.
       */
      dialogRef.afterClosed().subscribe(() => resolve(false));
    });

    debug('leave %s', leave);
    return leave;
  })();
};
